/**
 * Which anchor clicks the SPA router may swallow.
 *
 * The router listens on `document` and turns same-origin anchor clicks into
 * history navigations. That listener also hears **synthetic** clicks — the
 * `a.click()` that a blob download fires (see `topup-api.downloadInvoicePdf`) —
 * and used to swallow those too: `preventDefault()` cancelled the save, and the
 * unroutable blob path fell through the router's default case to `/play`. QA
 * 86cb9yd3p item 8: "you clicked on invoice it moves you to hero page".
 *
 * So the predicate lives here, on its own, with tests.
 */
import { legacyRedirect } from './site/site-kit';

/** True when the router should intercept this anchor and route in-app. */
export function shouldRouteInApp(a: HTMLAnchorElement): boolean {
  // Explicitly aimed elsewhere — a new tab, a frame, an opener name.
  if (a.target) return false;

  // A download is a file transfer, not a navigation. Never intercept: cancelling
  // it loses the file, and the href (often blob:) is not a route.
  if (a.hasAttribute('download')) return false;

  // Only real page URLs are routes. blob:, data:, mailto:, tel: are all handed
  // to the browser untouched — note that an <a> with a `blob:https://host/uuid`
  // href still reports OUR origin, so the origin check below cannot catch them.
  if (a.protocol !== 'http:' && a.protocol !== 'https:') return false;

  if (a.origin !== location.origin) return false;

  // A hash on the page we are already on is an in-page jump (the Codex's
  // chapter index, the landing's sections) — the browser scrolls to it; a
  // route change would remount the page for nothing.
  if (a.hash && a.pathname === location.pathname && a.search === location.search) return false;

  // Old static-page URLs are routes now (the router redirects them).
  if (legacyRedirect(a.pathname, a.search)) return true;

  // Real files (a PDF, an image, a download) are served as they are, not as
  // SPA routes — let the browser fetch them instead of falling through to /login.
  if (/\.[a-z0-9]+$/i.test(a.pathname)) return false;

  return true;
}
